import React, { useState, useEffect, useMemo } from 'react';
import { getSquadPillarScores, getPillarWeights } from '../api';
import { parseEinsteinRoute } from '../hooks/useEinsteinChat';
import EinsteinArc from '../components/EinsteinArc';
import EinsteinConsole from '../components/EinsteinConsole';
import PageChrome from '../components/PageChrome';

const PILLAR_KEYS = ['physical', 'technical', 'tactical', 'identity', 'availability'];
const DEFAULT_WEIGHTS = { physical: 30, technical: 30, tactical: 20, identity: 10, availability: 10 };

// Average of all active players' scores for a pillar, excluding NULLs from the average
// rather than counting them as 0 — same "no data ≠ 0" principle as everywhere else in this
// build. Null (not 0) if literally no active player has data for this pillar yet.
function squadAverage(rows, key) {
  const values = rows.map(r => r[key]).filter(v => v != null);
  if (!values.length) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

// Mirrors Stage 3's per-player overall formula exactly (pillarScores.js's
// computeOverallAndStars), just fed squad averages instead of one player's pillars — so the
// squad's star rating derives from the same weighted-average-of-non-null-pillars math.
// Weights are still fetched/used here purely for this calculation — the new bubble layout's
// sizing is fixed per-pillar (see PillarBubbleSpider's PILLAR_LAYOUT), not weight-driven.
function computeOverallAndStars(pillarAverages, weights) {
  const physicalForOverall = pillarAverages.physical == null ? null : Math.min(pillarAverages.physical, 100);
  const entries = PILLAR_KEYS.map(k => [k, k === 'physical' ? physicalForOverall : pillarAverages[k], weights[k]])
    .filter(([, val, w]) => val != null && w != null);
  // Same minimum-coverage gate as pillarScores.js's per-player formula (2026-07-22) — a
  // single present pillar renormalizes to 100% of the weight, which can fake a squad-level
  // rating off one pillar alone.
  if (entries.length < 2) return { overall: null, stars: null };
  const totalWeight = entries.reduce((s, [, , w]) => s + w, 0);
  const weightedSum = entries.reduce((s, [, val, w]) => s + val * w, 0);
  const overall = totalWeight > 0 ? weightedSum / totalWeight : null;
  const stars = overall == null ? null : Math.round((overall / 100 * 5) * 2) / 2;
  return { overall, stars };
}

export default function Home({ user, onNavigate }) {
  const [period, setPeriod] = useState('week');
  const [rows, setRows] = useState([]);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [loading, setLoading] = useState(true);
  // Router responses carry a real app URL (e.g. "/app?page=gps&gpsTab=match-chronic&player_id=82").
  // page/gpsTab drive which component App.js renders (its own React state, not URL-reactive
  // after mount — see App.js's handlePageChange comment) so this MUST go through the same
  // onNavigate/handlePageChange every other nav action uses, not a raw react-router navigate()
  // (that silently changed the URL without ever switching the page — the 2026-09-03 bug fix).
  // The remaining query params (player_id, date, ...) ride along as extraParams, merged into
  // the same setSearchParams call handlePageChange already makes.
  const goToRoute = route => {
    const { page, gpsTab, params } = parseEinsteinRoute(route);
    onNavigate(page, gpsTab || undefined, params);
  };

  useEffect(() => {
    getPillarWeights()
      .then(rows => {
        if (!rows?.length) return;
        const w = {};
        rows.forEach(r => { w[r.pillar] = Number(r.weight); });
        setWeights(w);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getSquadPillarScores(period)
      .then(data => {
        const n = v => (v === null || v === undefined ? null : Number(v));
        setRows((data || []).map(r => ({
          physical: n(r.physical), technical: n(r.technical), tactical: n(r.tactical),
          identity: n(r.identity), availability: n(r.availability),
        })));
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [period]);

  const squadPillars = useMemo(() => {
    const out = {};
    PILLAR_KEYS.forEach(k => { out[k] = squadAverage(rows, k); });
    return out;
  }, [rows]);

  const { stars } = useMemo(() => computeOverallAndStars(squadPillars, weights), [squadPillars, weights]);

  // Click-to-navigate on the pillar bubbles (2026-09-09, restored — briefly replaced 2026-09-03
  // by click-to-scope-the-Einstein-conversation per the redesign's Task 6, which is now dropped
  // in favor of this). Same gate as everywhere else (user.isAdmin || allowedTabs.includes(id)),
  // same destinations as before the redesign.
  const canAccess = tabId => !!(user?.isAdmin || (user?.allowedTabs || []).includes(tabId));
  // Einstein console (2026-09-10) — used to render unconditionally for anyone who reached Home
  // at all, since Einstein rode Home's own gate rather than having its own. Now a separate
  // permission (defaulted off for new non-admin accounts, granted per-account from Settings as
  // the feature gets tested more — explicit request), so Home has to check it independently
  // instead of assuming "on Home" implies "can use Einstein".
  const canAccessEinstein = canAccess('einstein');
  const bubbleLinks = {};
  if (canAccess('overview')) bubbleLinks.physical = () => onNavigate('overview');
  if (canAccess('availability')) bubbleLinks.availability = () => onNavigate('availability-analysis');
  if (canAccess('tactical-overview')) bubbleLinks.tactical = () => onNavigate('tactical-overview');
  if (canAccess('identity-overview')) bubbleLinks.identity = () => onNavigate('identity-overview');
  if (canAccess('technical-overview')) bubbleLinks.technical = () => onNavigate('technical-overview');

  return (
    <PageChrome onNavigate={onNavigate} user={user} contentMaxWidth={1180} padding="16px 32px 24px">
      {/* Home-only spacing below the nav bar — PillarBubbleSpider itself stays untouched (still
          used unchanged by Individual Report) so that page doesn't pick up this offset or the
          arc/console layout below.
          Fixed-height flex column (not just a marginTop/gap stack) — this is what makes "no
          scrolling to reach the composer" actually hold (2026-09-03 fix): the arc self-scales to
          the leftover height (its own bottomReserve), and the console gets `flex:1, minHeight:0`
          so IT — not the page — is what's height-bounded, which is what lets its own message
          list's overflowY:auto do the scrolling instead of the whole page growing. 120px below
          approximates the topbar + this page's own top padding/margin above this box; 520 is a
          floor so a very short window degrades to a normal scrolling page rather than crushing
          the console to nothing. */}
      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16, height: 'calc(100vh - 120px)', minHeight: 520 }}>
        {loading ? (
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>
        ) : (
          <>
            <EinsteinArc
              crestImageUrl={`${process.env.PUBLIC_URL}/heracles-crest.png`}
              title="Heracles Almelo"
              subtitle="Squad average"
              stars={stars}
              pillars={squadPillars}
              period={period}
              onPeriodChange={setPeriod}
              bubbleLinks={bubbleLinks}
              bottomReserve={canAccessEinstein ? 300 : 0}
            />
            {canAccessEinstein && (
              <div style={{ flex: 1, minHeight: 260 }}>
                <EinsteinConsole
                  user={user}
                  goToRoute={goToRoute}
                />
              </div>
            )}
          </>
        )}
      </div>
    </PageChrome>
  );
}
